import './AssignUserModal.scss';

import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Avatar,
  Box,
  IconButton,
  InputAdornment,
  Menu,
  TextField,
  Typography,
} from '@mui/material';
import { useDebounce } from 'hooks';
import React, { useMemo, useState } from 'react';
import {
  useFetchList,
  usePatchItem,
} from 'services/common/react-query-common-service';
import { MASTER_ENDPOINT } from 'utils/Constant';

import StylishLoader from '../components/StylishLoader';
import { JobAssignStatus, TeamUserRoles } from '../enum/TeamUserRoles';

interface User {
  id: string;
  priority?: number;
  teamId: string;
  user: {
    firstName: string;
    id: string;
    imageId?: string | null;
    lastName: string;
    login: {
      email: string;
    };
    teamUserRole: string;
  };
}

interface AssignUserModalProps {
  anchorEl: HTMLElement | null;
  jobData: any;
  onClose: () => void;
  open: boolean;
  userDetail: any;
  refetch: () => void;
  setIsAssigning?: (isAssigning: boolean) => void;
}

const AssignUserModal: React.FC<AssignUserModalProps> = ({
  anchorEl,
  jobData,
  onClose,
  open,
  userDetail,
  refetch,
  setIsAssigning,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const isGetTL = jobData?.jobAssignStatus === JobAssignStatus.TL_UNALLOCATED;
  const isTeamLeader = userDetail?.teamUserRole === TeamUserRoles.TEAM_LEADER;
  const { mutateAsync: patchItem } = usePatchItem();

  let endpoint = '';
  if (isGetTL) {
    endpoint = `${MASTER_ENDPOINT.Teams}/${String(
      jobData?.jobSourceId ?? '',
    )}/leaders`;
  } else {
    let extraParam = '';
    if (isTeamLeader) {
      extraParam = `?teamLeadId=${String(userDetail?.id ?? '')}`;
    }
    endpoint = `${MASTER_ENDPOINT.Teams}/leaders/members/${String(
      jobData?.jobSourceId ?? '',
    )}${extraParam}`;
  }

  const { data: teamMembers, isLoading } = useFetchList([endpoint], endpoint, {
    enabled: open && !!jobData?.jobSourceId && !!userDetail?.id,
    refetchOnMount: 'always',
  });

  const { primaryUsers, otherUsers } = useMemo(() => {
    const pUsers: User[] =
      (isGetTL ? teamMembers?.teamLeaders : teamMembers?.data) || [];
    const oUsers: User[] =
      (isGetTL
        ? teamMembers?.otherTeamLeaders
        : teamMembers?.otherRecruiters) || [];

    const filterAndSortUsers = (users: User[]) => {
      let filtered = users;
      if (debouncedSearchTerm) {
        const lowerSearch = debouncedSearchTerm.toLowerCase();
        filtered = users.filter(
          (item) =>
            item.user.firstName.toLowerCase().includes(lowerSearch) ||
            item.user.lastName.toLowerCase().includes(lowerSearch) ||
            item.user.login.email.toLowerCase().includes(lowerSearch),
        );
      }
      // Sort by priority: lower number = higher priority (1 comes first)
      return filtered.sort((a, b) => {
        const priorityA = a.priority ?? Number.MAX_SAFE_INTEGER;
        const priorityB = b.priority ?? Number.MAX_SAFE_INTEGER;
        return priorityA - priorityB;
      });
    };

    return {
      otherUsers: filterAndSortUsers(oUsers),
      primaryUsers: filterAndSortUsers(pUsers),
    };
  }, [teamMembers, debouncedSearchTerm, isGetTL]);

  const handleUserClick = async (
    userId: string,
    teamId: string,
    teamUserRole: string,
  ) => {
    onClose();
    // const role = isGetTL ? TeamUserRoles.TEAM_LEADER : TeamUserRoles.RECRUITER;
    const payload = {
      assignToUserRole: teamUserRole,
      assignedTo: userId,
      removedUsers: [],
      teamId: teamId,
    };

    try {
      setIsAssigning?.(true);
      await patchItem({
        data: payload,
        endpoint: `${MASTER_ENDPOINT.Jobs}/${MASTER_ENDPOINT.JobQueue}/assign`,
        id: String(jobData?.id ?? ''),
      });
      refetch();
    } catch (error) {
      console.error('Error assigning user:', error);
    } finally {
      setIsAssigning?.(false);
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) ?? ''}${
      lastName?.charAt(0) ?? ''
    }`.toUpperCase();
  };

  const getAvatarColor = (index: number) => {
    const colors = ['#EBF0FF', '#E0F2FE', '#F0FDF4', '#FEF2F2', '#FEF3C7'];
    return colors[index % colors.length];
  };

  return (
    <Menu
      PaperProps={{
        className: 'assign-user-menu',
      }}
      disableAutoFocusItem={true}
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'right',
        vertical: 'bottom',
      }}
      onClose={onClose}
      open={open}
      transformOrigin={{
        horizontal: 'right',
        vertical: 'top',
      }}>
      <Box className="assign-user-modal">
        <Typography className="modal-title">Assign to</Typography>

        {/* Search Box */}
        <Box className="search-container">
          <TextField
            autoFocus
            fullWidth
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder="Search name"
            value={searchTerm}
            className="search-input"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className="search-icon" />
                </InputAdornment>
              ),
            }}
          />
          <IconButton className="filter-button">
            <TuneIcon />
          </IconButton>
        </Box>

        {/* User List */}
        <Box className="user-list">
          {isLoading ? (
            <StylishLoader text="Loading users..." mode="inline" />
          ) : primaryUsers.length > 0 || otherUsers.length > 0 ? (
            <>
              {primaryUsers.map((item: User, index) => (
                <Box
                  key={item?.user?.id || index}
                  className="user-item"
                  onClick={() =>
                    handleUserClick(
                      item?.user?.id,
                      item?.teamId,
                      item?.user?.teamUserRole,
                    )
                  }>
                  <Avatar
                    className="user-avatar"
                    sx={{ backgroundColor: getAvatarColor(index) }}>
                    <span className="avatar-initials">
                      {getInitials(
                        item?.user?.firstName || '',
                        item?.user?.lastName || '',
                      )}
                    </span>
                  </Avatar>
                  <Box className="user-info">
                    <Typography className="user-name">
                      {item?.user?.firstName} {item?.user?.lastName}
                    </Typography>
                    <Typography className="user-email">
                      {item?.user?.login?.email}
                    </Typography>
                  </Box>
                </Box>
              ))}

              {otherUsers.length > 0 && (
                <>
                  <Typography className="other-member-title">
                    Other Members
                  </Typography>
                  {otherUsers.map((item: User, index) => (
                    <Box
                      key={item?.user?.id || index}
                      className="user-item"
                      onClick={() =>
                        handleUserClick(
                          item?.user?.id,
                          item?.teamId,
                          item?.user?.teamUserRole,
                        )
                      }>
                      <Avatar
                        className="user-avatar"
                        sx={{
                          backgroundColor: getAvatarColor(
                            index + primaryUsers.length,
                          ),
                        }}>
                        <span className="avatar-initials">
                          {getInitials(
                            item?.user?.firstName || '',
                            item?.user?.lastName || '',
                          )}
                        </span>
                      </Avatar>
                      <Box className="user-info">
                        <Typography className="user-name">
                          {item?.user?.firstName} {item?.user?.lastName}
                        </Typography>
                        <Typography className="user-email">
                          {item?.user?.login?.email}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </>
              )}
            </>
          ) : (
            <Typography className="no-results">No users found</Typography>
          )}
        </Box>
      </Box>
    </Menu>
  );
};

export default AssignUserModal;
